import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, X, Send, Copy, Phone, MapPin, Briefcase, GraduationCap, Languages, Clock, Calendar, User, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const STATUSES = [
  { key: "all", label: "Все" },
  { key: "new", label: "Новые" },
  { key: "reviewing", label: "На рассмотрении" },
  { key: "accepted", label: "Приняты" },
  { key: "rejected", label: "Отклонены" },
];

const STATUS_BADGE = {
  new: { label: "Новая", cls: "bg-blue-100 text-blue-700" },
  reviewing: { label: "На рассмотрении", cls: "bg-amber-100 text-amber-700" },
  accepted: { label: "Принята", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Отклонена", cls: "bg-red-100 text-red-700" },
};

const DETAIL_FIELDS = [
  { key: "age", label: "Возраст", icon: User },
  { key: "phone", label: "Телефон", icon: Phone },
  { key: "address", label: "Адрес проживания", icon: MapPin },
  { key: "position", label: "Желаемая должность", icon: Briefcase },
  { key: "experience", label: "Опыт работы", icon: Briefcase },
  { key: "education", label: "Образование", icon: GraduationCap },
  { key: "languages", label: "Языки", icon: Languages },
  { key: "schedule", label: "График работы", icon: Clock },
  { key: "about", label: "О себе", icon: User },
  { key: "start_date", label: "Готов приступить", icon: Calendar },
];

function TelegramConnect() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/job-telegram-link").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const copy = () => { if (data?.link) { navigator.clipboard.writeText(data.link); toast.success("Ссылка скопирована"); } };
  const removeRecipient = async (id) => {
    if (!window.confirm("Отключить этого получателя?")) return;
    await api.delete("/admin/job-recipients", { data: { id } });
    load();
  };
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <span className="font-display font-black flex items-center gap-2 text-black"><Send size={16} className="text-[#E10514]" /> Telegram для заявок</span>
        <span className="text-xs font-bold text-gray-500">{data?.recipients?.length || 0} подключено {open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-gray-500 leading-relaxed">
            Отдельное подключение только для заявок на работу. Откройте ссылку в Telegram (или перешлите ответственному сотруднику) — новые анкеты с фото будут приходить в этот чат.
          </p>
          {data?.link ? (
            <div className="flex gap-2">
              <input readOnly value={data.link} className="admin-input text-xs flex-1" data-testid="job-tg-link" />
              <button className="admin-btn admin-btn-ghost" onClick={copy}><Copy size={14} /></button>
              <a className="admin-btn" href={data.link} target="_blank" rel="noreferrer">Открыть</a>
            </div>
          ) : (
            <div className="text-xs text-amber-600 font-bold">Username бота не задан в настройках Telegram — ссылку сформировать нельзя.</div>
          )}
          {data?.recipients?.length > 0 && (
            <div className="space-y-1.5">
              {data.recipients.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-[#FBF3E4]/60 rounded-lg px-3 py-2 text-sm">
                  <span className="font-bold">{r.name || "Получатель"} {r.telegram_username ? `@${r.telegram_username}` : ""}</span>
                  <button onClick={() => removeRecipient(r.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function JobApplications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/job-applications", { params: { status } })
      .then((r) => { setItems(r.data.applications || []); setCounts(r.data.counts || {}); })
      .catch(() => toast.error("Не удалось загрузить заявки"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const setAppStatus = async (id, newStatus) => {
    setBusy(true);
    await api.put("/admin/job-applications", { id, status: newStatus });
    setBusy(false);
    setActive((a) => (a && a.id === id ? { ...a, status: newStatus } : a));
    load();
    toast.success("Статус обновлён");
  };
  const remove = async (id) => {
    if (!window.confirm("Удалить заявку безвозвратно?")) return;
    await api.delete("/admin/job-applications", { data: { id } });
    setActive(null);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-2xl">Заявки на работу</h1>
        <div className="flex items-center gap-2">
          <a className="admin-btn admin-btn-ghost" href="/jobs-flyer" target="_blank" rel="noreferrer" data-testid="jobs-flyer-link"><Briefcase size={15} /> Флаер вакансий</a>
          <button className="admin-btn admin-btn-ghost" onClick={load} data-testid="jobs-refresh-btn"><RefreshCw size={15} /> Обновить</button>
        </div>
      </div>

      <TelegramConnect />

      {/* Фильтр статусов */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)} data-testid={`jobs-filter-${s.key}`}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-colors ${status === s.key ? "bg-[#111] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
            {s.label} {s.key === "all" ? (counts.total || 0) : (counts[s.key] || 0)}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-gray-400 font-bold">Заявок пока нет</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((a) => {
            const b = STATUS_BADGE[a.status] || STATUS_BADGE.new;
            return (
              <button key={a.id} onClick={() => setActive(a)} data-testid={`job-card-${a.id}`}
                className="bg-white rounded-2xl p-4 shadow-sm text-left hover:shadow-md transition-shadow flex gap-3">
                {a.photo ? (
                  <img src={a.photo} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><User className="text-gray-300" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black text-black truncate">{a.fio}</div>
                  <div className="text-sm text-[#E10514] font-bold truncate">{a.position}</div>
                  <div className="text-xs text-gray-500 truncate">{a.phone}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
                    <span className="text-[10px] text-gray-400">{a.date}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Детальная модалка */}
      {active && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-5" onClick={() => setActive(null)}>
          <div className="bg-white w-full sm:max-w-[560px] sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
              <span className="font-display font-black">Анкета соискателя</span>
              <button onClick={() => setActive(null)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-4 items-center">
                {active.photo && <img src={active.photo} alt="" className="w-24 h-24 rounded-2xl object-cover border-4 border-[#FEBC10]" />}
                <div className="min-w-0">
                  <div className="font-display font-black text-xl text-black">{active.fio}</div>
                  <div className="text-[#E10514] font-bold">{active.position}</div>
                  <div className="text-xs text-gray-400 mt-1">{active.date}</div>
                </div>
              </div>

              <div className="space-y-2.5">
                {DETAIL_FIELDS.filter((f) => f.key !== "position" && (active[f.key] || "").toString().trim()).map((f) => (
                  <div key={f.key} className="flex gap-2.5">
                    <f.icon size={16} className="text-gray-400 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">{f.label}</div>
                      <div className="text-sm text-black whitespace-pre-wrap break-words">{active[f.key]}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Управление статусом */}
              <div className="pt-2 border-t">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">Статус заявки</div>
                <div className="flex flex-wrap gap-2">
                  {["new", "reviewing", "accepted", "rejected"].map((st) => {
                    const b = STATUS_BADGE[st];
                    const on = active.status === st;
                    return (
                      <button key={st} disabled={busy} onClick={() => setAppStatus(active.id, st)} data-testid={`job-status-${st}`}
                        className={`text-xs font-black px-3 py-1.5 rounded-full flex items-center gap-1 ${on ? b.cls + " ring-2 ring-offset-1 ring-current" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                        {on && <CheckCircle2 size={13} />} {b.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                {active.phone && <a href={`tel:${active.phone}`} className="admin-btn flex-1 justify-center"><Phone size={15} /> Позвонить</a>}
                <button onClick={() => remove(active.id)} className="admin-btn admin-btn-ghost text-red-600" data-testid="job-delete-btn"><Trash2 size={15} /> Удалить</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
